import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  PurchaseOrderEvent,
  PurchaseOrderEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/trade/purchase_order_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { PurchaseOrderCreatedDetail } from './PurchaseOrderCreatedDetail';
import { PurchaseOrderDeletedDetail } from './PurchaseOrderDeletedDetail';
import { PurchaseOrderStatusChangedDetail } from './PurchaseOrderStatusChangedDetail';
import { PurchaseOrderUpdatedDetail } from './PurchaseOrderUpdatedDetail';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type PurchaseOrderEventDetailProps = {
  payload: string;
};

export const PurchaseOrderEventDetail = ({
  payload,
}: PurchaseOrderEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): PurchaseOrderEvent | null => {
    try {
      return fromJson(PurchaseOrderEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return (
      <JsonViewer data={payload} title={t.eventLogs.rawPayloadParseError} />
    );
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <PurchaseOrderCreatedDetail event={inner.value} />;
      case 'updated':
        return <PurchaseOrderUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <PurchaseOrderDeletedDetail event={inner.value} />;
      case 'statusChanged':
        return <PurchaseOrderStatusChangedDetail event={inner.value} />;
      case undefined:
        return (
          <JsonViewer data={payload} title={t.eventLogs.unknownEventType} />
        );
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.eventLogs.purchaseOrder.events.created;
      case 'updated':
        return t.eventLogs.purchaseOrder.events.updated;
      case 'deleted':
        return t.eventLogs.purchaseOrder.events.deleted;
      case 'statusChanged':
        return t.eventLogs.purchaseOrder.events.statusChanged;
      case undefined:
        return t.eventLogs.unknownEvent;
    }
  };

  return (
    <Col className="@PurchaseOrderEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
